import { writable } from 'svelte/store';
import { goto } from '$app/navigation';
import axios from 'axios';
import type { BriyahMode } from '@shared/types/app.types';

// Create writable stores for user authentication
const userId = writable<string | null>(null);
const phoneNumber = writable<string | null>(null);
const isAuthenticated = writable<boolean>(false);
const mode = writable<BriyahMode | null>(null);

// Create writable stores for agent authentication
export const agentId = writable<string | null>(null);

// Create writable stores for room authentication
export const roomId = writable<string | null>(null);

export const authStore = {
	// Subscribe methods to make these reactive
	subscribe: (run: (value: { userId: string | null; phoneNumber: string | null; isAuthenticated: boolean; mode: BriyahMode | null }) => void) => {
		let currentUserId: string | null = null;
		let currentPhoneNumber: string | null = null;
		let currentAuth = false;
		let currentMode: BriyahMode | null = null;

		const unsubscribeUserId = userId.subscribe((value) => {
			currentUserId = value;
			run({ userId: currentUserId, phoneNumber: currentPhoneNumber, isAuthenticated: currentAuth, mode: currentMode });
		});

		const unsubscribePhoneNumber = phoneNumber.subscribe((value) => {
			currentPhoneNumber = value;
			run({ userId: currentUserId, phoneNumber: currentPhoneNumber, isAuthenticated: currentAuth, mode: currentMode });
		});

		const unsubscribeAuth = isAuthenticated.subscribe((value) => {
			currentAuth = value;
			run({ userId: currentUserId, phoneNumber: currentPhoneNumber, isAuthenticated: currentAuth, mode: currentMode });
		});

		const unsubscribeMode = mode.subscribe((value) => {
			currentMode = value;
			run({ userId: currentUserId, phoneNumber: currentPhoneNumber, isAuthenticated: currentAuth, mode: currentMode });
		});

		return () => {
			unsubscribeUserId();
			unsubscribePhoneNumber();
			unsubscribeAuth();
			unsubscribeMode();
		};
	},

	// Getter methods for direct access
	get userId() {
		let value: string | null = null;
		userId.subscribe((v) => (value = v))();
		return value;
	},

	get phoneNumber() {
		let value: string | null = null;
		phoneNumber.subscribe((v) => (value = v))();
		return value;
	},

	get isAuthenticated() {
		let value = false;
		isAuthenticated.subscribe((v) => (value = v))();
		return value;
	},

	get mode() {
		let value: BriyahMode | null = null;
		mode.subscribe((v) => (value = v))();
		return value;
	},

	/**
	 * Send OTP for phone login
	 * @param phone The phone number (normalized 10 digits)
	 * @returns Promise with success status and message
	 */
	async sendLoginOtp(phone: string): Promise<{ success: boolean; message?: string; remainingAttempts?: number }> {
		try {
			const response = await axios.post('/api/auth/phone-login/send-otp', {
				phoneNumber: phone
			}, {
				withCredentials: true
			});

			return {
				success: response.data.success,
				message: response.data.message,
				remainingAttempts: response.data.remainingAttempts
			};
		} catch (error: any) {
			console.error('Send login OTP failed:', error);
			const errorMessage = error.response?.data?.message || 'Failed to send verification code';
			return { success: false, message: errorMessage };
		}
	},

	/**
	 * Verify OTP and login
	 * @param phone The phone number (normalized 10 digits)
	 * @param code The 6-digit OTP code
	 * @param loginMode Which surface the session is for
	 * @returns Promise with success status
	 */
	async verifyLoginOtp(phone: string, code: string, loginMode: BriyahMode): Promise<{ success: boolean; error?: string }> {
		try {
			const response = await axios.post('/api/auth/phone-login/verify-otp', {
				phoneNumber: phone,
				code,
				mode: loginMode
			}, {
				withCredentials: true // JWT cookie automatically set by browser
			});

			// JWT cookie automatically set by browser
			userId.set(response.data.userId);
			phoneNumber.set(response.data.phoneNumber);
			isAuthenticated.set(true);
			mode.set(response.data.mode || 'agents');

			return { success: true };
		} catch (error: any) {
			console.error('Verify login OTP failed:', error);
			const errorMessage = error.response?.data?.message || 'Failed to verify code';
			return { success: false, error: errorMessage };
		}
	},

	async logout(): Promise<void> {
		try {
			await axios.post('/api/auth/logout', {}, {
				withCredentials: true // Send cookies
			});
		} catch (error) {
			console.error('Logout failed:', error);
		}

		// Clear stores
		userId.set(null);
		phoneNumber.set(null);
		isAuthenticated.set(false);
		mode.set(null);
		goto('/');
	},

	async checkAuth(): Promise<boolean> {
		try {
			// JWT cookie automatically sent
			const response = await axios.get('/api/auth/session', {
				withCredentials: true
			});

			if (response.data.userId) {
				userId.set(response.data.userId);
				phoneNumber.set(response.data.phoneNumber);
				isAuthenticated.set(true);
				mode.set(response.data.mode || 'agents');
				return true;
			}
		} catch (error) {
			// Not authenticated
		}

		userId.set(null);
		phoneNumber.set(null);
		isAuthenticated.set(false);
		mode.set(null);
		return false;
	}
};

